import * as THREE from 'three';
import { Panel } from '../ui/panel';
import { acquireMenuPointer, releaseMenuPointer } from '../ui/pointer';
import { placePanelInFront } from '../ui/placement';
import type { PanelButton } from '../ui/button';
import {
  contentBounds,
  numericFont,
  onAssetsReady,
  paintPageTitleRow,
  treatiseBodyFont,
} from '../ui/style';
import { difficultyLabel, t } from '../i18n';
import { drillTargetName } from './strokeLabels';
import { loadArcadeBest } from './arcadeBest';
import { isFirebaseConfigured } from '../firebase/config';
import type { LeaderboardView } from '../firebase/leaderboard';
import type {
  ArcadeStats,
  DrillReasonKey,
  DrillStats,
  PellStats,
  SessionStats,
  StrokeBreakdown,
  TargetsStats,
} from './stats';
import {
  CUT_ROSE_SECTORS,
  PELL_ZONE_COLORS,
  PELL_ZONE_ORDER,
  SUMMARY_PANEL_HEIGHT_M,
  SUMMARY_PANEL_WIDTH_M,
  UI_BAD,
  UI_GOOD,
  UI_INK,
  UI_INK_MUTED,
  UI_SEAL_RED,
  ZONE,
  type Zone,
} from '../config/constants';

export type SummaryHandlers = {
  onRestart: () => void;
  onAgain: () => void;
};

/** Per-run summary options. `competitive` is false for a free (no-timer) arcade practice run. */
export type SummaryOptions = {
  competitive?: boolean;
};

/** A rectangular sub-area of the canvas a tab draws its content into. */
type Rect = { x: number; y: number; w: number; h: number };

type TabDef = {
  id: string;
  label: string;
  draw: (c: CanvasRenderingContext2D, ch: number, area: Rect) => void;
};

/**
 * End-of-session panel. Content is split into tabs (one concern each, no overlap); the
 * action row (Ripeti / Altra) stays pinned at the bottom across tabs.
 */
export class SummaryPanel {
  private panel: Panel | null = null;
  private unsubscribe: (() => void) | null = null;

  private handlers: SummaryHandlers | null = null;
  private tabs: TabDef[] = [];
  private activeTab = '';

  // Arcade ("Sfida") leaderboard: filled asynchronously after the run ends (see
  // `setLeaderboard`). `loading` is true only while we expect data (Firebase configured).
  private leaderboard: LeaderboardView | null = null;
  private leaderboardLoading = false;
  /** False for a free (no-timer) arcade practice run — no persistence, best, or leaderboard. */
  private competitive = true;

  constructor(
    private scene: THREE.Scene,
    private camera: THREE.Camera,
  ) {}

  show(stats: SessionStats, handlers: SummaryHandlers, opts: SummaryOptions = {}): void {
    if (this.panel) return;
    this.handlers = handlers;
    this.competitive = opts.competitive ?? true;
    // A fresh run: the leaderboard (competitive arcade only) starts empty and "loading" while
    // the controller submits the score and fetches the rank in the background.
    this.leaderboard = null;
    this.leaderboardLoading = stats.kind === 'arcade' && this.competitive && isFirebaseConfigured();
    this.tabs = this.buildTabs(stats);
    this.activeTab = this.tabs[0]?.id ?? '';

    const panel = new Panel({ widthM: SUMMARY_PANEL_WIDTH_M, heightM: SUMMARY_PANEL_HEIGHT_M });
    this.panel = panel;

    this.unsubscribe = onAssetsReady(() => this.rebuild());
    this.rebuild();

    placePanelInFront(panel.mesh, this.camera);
    this.scene.add(panel.mesh);
    acquireMenuPointer();
  }

  hide(): void {
    if (!this.panel) return;
    this.unsubscribe?.();
    this.unsubscribe = null;
    releaseMenuPointer();
    this.scene.remove(this.panel.mesh);
    this.panel.dispose();
    this.panel = null;
  }

  private buildTabs(stats: SessionStats): TabDef[] {
    if (stats.kind === 'drill') {
      return [
        {
          id: 'drill',
          label: t('summaryDrillTab'),
          draw: (c, ch, a) => drawDrillTab(c, ch, stats, a),
        },
      ];
    }
    if (stats.kind === 'targets') {
      return [
        {
          id: 'sum',
          label: t('summaryTabSummary'),
          draw: (c, ch, a) => drawTargetsTab(c, ch, stats, a),
        },
      ];
    }
    if (stats.kind === 'arcade') {
      // Snapshot the best now: a competitive run's `end()` already updated it, so this reflects
      // the just-finished run; a free run leaves it at the prior record.
      const best = loadArcadeBest(stats.technique, stats.difficulty);
      const competitive = this.competitive;
      const tabs: TabDef[] = [
        {
          id: 'sum',
          label: t('summaryTabSummary'),
          draw: (c, ch, a) => drawArcadeTab(c, ch, stats, best, competitive, a),
        },
      ];
      // The shared leaderboard exists only for a competitive run with Firebase configured.
      if (competitive && isFirebaseConfigured()) {
        tabs.push({
          id: 'class',
          label: t('summaryTabLeaderboard'),
          draw: (c, ch, a) => this.drawLeaderboardTab(c, ch, a),
        });
      }
      return tabs;
    }
    return [
      {
        id: 'sum',
        label: t('summaryTabSummary'),
        draw: (c, ch, a) => drawPellSummaryTab(c, ch, stats, a),
      },
      {
        id: 'tec',
        label: t('summaryTabTechnique'),
        draw: (c, ch, a) => drawTechniqueTab(c, ch, stats, a),
      },
    ];
  }

  /** Rebuild buttons (tab row + actions) and repaint the active tab's content. */
  private rebuild(): void {
    const panel = this.panel;
    const handlers = this.handlers;
    if (!panel || !handlers) return;
    const cw = panel.canvas.width;
    const ch = panel.canvas.height;
    const b = contentBounds(cw, ch);
    const showTabs = this.tabs.length > 1;

    const buttons: PanelButton[] = [];

    if (showTabs) {
      const n = this.tabs.length;
      const gap = cw * 0.012;
      const tw = (b.w - gap * (n - 1)) / n;
      this.tabs.forEach((tab, i) => {
        buttons.push({
          rect: { x: b.x + i * (tw + gap), y: ch * 0.16, w: tw, h: ch * 0.075 },
          label: tab.label,
          variant: tab.id === this.activeTab ? 'primary' : 'secondary',
          onClick: () => {
            this.activeTab = tab.id;
            this.rebuild();
          },
        });
      });
    }

    const aw = cw * 0.26;
    const ah = ch * 0.12;
    const ay = ch * 0.84;
    buttons.push(
      {
        rect: { x: cw * 0.21, y: ay, w: aw, h: ah },
        label: t('summaryRestart'),
        variant: 'primary',
        onClick: () => {
          this.hide();
          handlers.onRestart();
        },
      },
      {
        rect: { x: cw * 0.53, y: ay, w: aw, h: ah },
        label: t('summaryAgain'),
        variant: 'secondary',
        onClick: () => {
          this.hide();
          handlers.onAgain();
        },
      },
    );

    panel.setButtons(buttons);
    this.repaintContent();
  }

  private repaintContent(): void {
    const panel = this.panel;
    if (!panel) return;
    const cw = panel.canvas.width;
    const ch = panel.canvas.height;
    const b = contentBounds(cw, ch);
    panel.renderFrame();
    const c = panel.getContext();
    paintPageTitleRow(c, 'Riepilogo', cw, ch);

    const showTabs = this.tabs.length > 1;
    const top = showTabs ? ch * 0.27 : ch * 0.2;
    const area: Rect = { x: b.x, y: top, w: b.w, h: ch * 0.8 - top };

    const tab = this.tabs.find((t) => t.id === this.activeTab) ?? this.tabs[0];
    tab?.draw(c, ch, area);

    panel.paintButtons(c);
    panel.markDirty();
  }

  /**
   * Supply the post-run leaderboard once the async submit+fetch resolves. `null` marks a
   * failed/unavailable fetch (e.g. offline). No-op if the panel was already dismissed.
   */
  setLeaderboard(view: LeaderboardView | null): void {
    if (!this.panel) return;
    this.leaderboard = view;
    this.leaderboardLoading = false;
    this.repaintContent();
  }

  /** Classifica tab: "you are #N" + the top and neighbouring rows. Reads live async state. */
  private drawLeaderboardTab(c: CanvasRenderingContext2D, ch: number, area: Rect): void {
    const center = (msg: string): void => {
      c.save();
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      c.fillStyle = UI_INK_MUTED;
      c.font = treatiseBodyFont(ch * 0.044, 400);
      c.fillText(msg, area.x + area.w * 0.5, area.y + area.h * 0.4);
      c.restore();
    };

    if (this.leaderboardLoading) return center(t('leaderboardLoading'));
    const view = this.leaderboard;
    if (!view) return center(t('leaderboardUnavailable'));
    if (view.rows.length === 0) return center(t('leaderboardEmpty'));

    // Banner: your rank out of the field.
    c.save();
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    if (view.rank != null) {
      c.fillStyle = UI_SEAL_RED;
      c.font = numericFont(ch * 0.1, 700);
      c.fillText(`#${view.rank}`, area.x + area.w * 0.5, area.y + ch * 0.05);
    }
    c.fillStyle = UI_INK_MUTED;
    c.font = treatiseBodyFont(ch * 0.036, 600);
    c.fillText(t('leaderboardPlayers')(view.total), area.x + area.w * 0.5, area.y + ch * 0.12);
    c.restore();

    drawLeaderboardRows(c, ch, view, {
      x: area.x,
      y: area.y + ch * 0.17,
      w: area.w,
      h: area.h - ch * 0.17,
    });
  }
}

// ─────────────────────────────────────────────────────────────
// Tab content (each owns its area; no shared layout, no overlap)
// ─────────────────────────────────────────────────────────────

function drawPellSummaryTab(
  c: CanvasRenderingContext2D,
  ch: number,
  stats: PellStats,
  area: Rect,
): void {
  const left: Rect = { x: area.x, y: area.y, w: area.w * 0.44, h: area.h };
  const right: Rect = { x: area.x + area.w * 0.5, y: area.y, w: area.w * 0.5, h: area.h };
  drawStatLines(
    c,
    ch,
    [
      { label: t('summaryTotal'), value: `${stats.total}` },
      { label: t('summaryThrusts'), value: `${stats.thrusts}` },
      { label: t('summaryEdgeGood'), value: `${stats.edgeGoodPct.toFixed(0)}%` },
      { label: t('summaryAvgVelocity'), value: `${stats.avgTipSpeed.toFixed(1)} m/s` },
    ],
    left,
  );
  drawZoneBars(
    c,
    ch,
    PELL_ZONE_ORDER.map((z) => ({
      label: zoneLabel(z),
      frac: 0, // filled below from max
      valueText: `${stats.zoneDistribution[z]}`,
      color: zoneColor(z),
      raw: stats.zoneDistribution[z],
    })),
    right,
    true,
  );
}

function drawTargetsTab(
  c: CanvasRenderingContext2D,
  ch: number,
  stats: TargetsStats,
  area: Rect,
): void {
  const left: Rect = { x: area.x, y: area.y, w: area.w * 0.46, h: area.h };
  const right: Rect = { x: area.x + area.w * 0.52, y: area.y, w: area.w * 0.48, h: area.h };
  drawStatLines(
    c,
    ch,
    [
      {
        label: t('summaryHitRate'),
        value: `${stats.hitRatePct.toFixed(0)}% (${stats.hits}/${stats.total})`,
      },
      { label: t('summaryAvgReaction'), value: `${(stats.avgReactionMs / 1000).toFixed(2)} s` },
      { label: t('summaryAvgVelocity'), value: `${stats.avgTipSpeed.toFixed(1)} m/s` },
      { label: t('summaryAvgAccuracy'), value: `${(stats.avgAccuracyM * 100).toFixed(1)} cm` },
    ],
    left,
  );
  drawZoneBars(
    c,
    ch,
    PELL_ZONE_ORDER.map((z) => ({
      label: zoneLabel(z),
      frac: stats.hitRateByZone[z] / 100,
      valueText: stats.spawnsByZone[z] > 0 ? `${stats.hitRateByZone[z].toFixed(0)}%` : '—',
      color: zoneColor(z),
      raw: stats.hitRateByZone[z],
    })),
    right,
    false,
  );
}

/** Arcade result: score + max combo + accuracy, plus the per-technique quality diagnostic. */
function drawArcadeTab(
  c: CanvasRenderingContext2D,
  ch: number,
  stats: ArcadeStats,
  best: number,
  competitive: boolean,
  area: Rect,
): void {
  // A free practice run is never recorded — show the score plainly, no "new best".
  const isNewBest = competitive && stats.score > 0 && stats.score >= best;

  // Score banner — the headline number, centered.
  c.save();
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillStyle = UI_SEAL_RED;
  c.font = numericFont(ch * 0.13, 700);
  c.fillText(`${stats.score}`, area.x + area.w * 0.5, area.y + ch * 0.07);

  c.fillStyle = UI_INK_MUTED;
  c.font = treatiseBodyFont(ch * 0.038, 600);
  c.fillText(
    isNewBest ? t('summaryNewBest') : t('summaryScore'),
    area.x + area.w * 0.5,
    area.y + ch * 0.16,
  );
  c.restore();

  const lines: Array<{ label: string; value: string }> = [];
  lines.push({ label: t('summaryDifficulty'), value: difficultyLabel(stats.difficulty) });
  if (competitive) lines.push({ label: t('summaryBest'), value: `${best}` });
  lines.push({ label: t('summaryMaxCombo'), value: `${stats.maxCombo}` });
  lines.push({
    label: t('summaryHitRate'),
    value: `${stats.accuracyPct.toFixed(0)}% (${stats.hits}/${stats.total})`,
  });
  if (stats.technique === 'thrust') {
    lines.push({
      label: t('summaryAvgAccuracy'),
      value: `${(stats.avgAccuracyM * 100).toFixed(1)} cm`,
    });
    lines.push({
      label: t('summaryAvgReaction'),
      value: `${(stats.avgReactionMs / 1000).toFixed(2)} s`,
    });
  } else {
    lines.push({ label: t('summaryDrillAvgEdge'), value: `${stats.avgEdgeDeg.toFixed(0)}°` });
  }

  const bodyTop = area.y + ch * 0.22;
  drawStatLines(c, ch, lines, {
    x: area.x + area.w * 0.18,
    y: bodyTop,
    w: area.w * 0.64,
    h: area.h - ch * 0.22,
  });
}

/** Leaderboard rows (rank · nickname · score), with the player's row highlighted. */
function drawLeaderboardRows(
  c: CanvasRenderingContext2D,
  ch: number,
  view: LeaderboardView,
  r: Rect,
): void {
  const rows = view.rows;
  const rowH = Math.min(ch * 0.075, r.h / Math.max(rows.length, 1));
  const rankW = r.w * 0.16;
  const scoreW = r.w * 0.26;
  const nameX = r.x + rankW;
  const scoreX = r.x + r.w;
  const nameMaxW = scoreX - scoreW - nameX - 8;

  c.save();
  c.textBaseline = 'middle';
  let prevRank = 0;
  rows.forEach((row, i) => {
    const y = r.y + i * rowH + rowH * 0.5;

    // Gap marker between non-contiguous rank blocks (e.g. the top rows … your neighbours).
    if (prevRank > 0 && row.rank - prevRank > 1) {
      c.fillStyle = UI_INK_MUTED;
      c.textAlign = 'center';
      c.font = treatiseBodyFont(ch * 0.04, 400);
      c.fillText('⋯', r.x + r.w * 0.5, y - rowH * 0.5);
    }
    prevRank = row.rank;

    if (row.isMe) {
      c.fillStyle = 'rgba(150,30,20,0.12)'; // faint seal-red highlight for your row
      c.fillRect(r.x - rowH * 0.1, y - rowH * 0.42, r.w + rowH * 0.2, rowH * 0.84);
    }
    const ink = row.isMe ? UI_SEAL_RED : UI_INK;

    c.fillStyle = ink;
    c.textAlign = 'left';
    c.font = numericFont(ch * 0.038, 700);
    c.fillText(`#${row.rank}`, r.x, y);

    c.fillStyle = row.isMe ? UI_SEAL_RED : UI_INK_MUTED;
    c.font = treatiseBodyFont(ch * 0.038, row.isMe ? 600 : 400);
    c.fillText(clipText(c, row.nickname, nameMaxW), nameX, y);

    c.fillStyle = ink;
    c.textAlign = 'right';
    c.font = numericFont(ch * 0.04, 700);
    c.fillText(`${row.score}`, scoreX, y);
  });
  c.restore();
}

/** Truncate `text` with an ellipsis to fit `maxW` under the canvas' current font. */
function clipText(c: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (maxW <= 0 || c.measureText(text).width <= maxW) return text;
  let s = text;
  while (s.length > 1 && c.measureText(`${s}…`).width > maxW) s = s.slice(0, -1);
  return `${s}…`;
}

/** Prescriptive-drill result: target colpo, key numbers, and the dominant failure causes. */
function drawDrillTab(
  c: CanvasRenderingContext2D,
  ch: number,
  stats: DrillStats,
  area: Rect,
): void {
  // Header: which colpo was drilled.
  c.save();
  c.textBaseline = 'middle';
  c.textAlign = 'left';
  c.fillStyle = UI_INK_MUTED;
  c.font = treatiseBodyFont(ch * 0.042, 600);
  c.fillText(drillTargetName(stats.target), area.x, area.y + ch * 0.02);
  c.restore();

  const bodyTop = area.y + ch * 0.1;
  const left: Rect = { x: area.x, y: bodyTop, w: area.w * 0.42, h: area.h - ch * 0.1 };
  const right: Rect = {
    x: area.x + area.w * 0.5,
    y: bodyTop,
    w: area.w * 0.5,
    h: area.h - ch * 0.1,
  };

  drawStatLines(
    c,
    ch,
    [
      { label: t('summaryDrillCorrect'), value: `${stats.correct}/${stats.attempts}` },
      { label: t('summaryDrillAccuracy'), value: `${stats.accuracyPct.toFixed(0)}%` },
      { label: t('summaryDrillTime'), value: formatMMSS(stats.elapsedMs) },
      { label: t('summaryDrillAvgEdge'), value: `${stats.avgEdgeDeg.toFixed(0)}°` },
    ],
    left,
  );

  drawReasonBars(c, ch, stats.reasonCounts, right);
}

const DRILL_REASON_ORDER: readonly DrillReasonKey[] = [
  'zona',
  'lato',
  'inclinazione',
  'filo',
  'piattonata',
  'ambiguo',
];

/** Horizontal bars for the failure-cause breakdown (only non-zero causes). */
function drawReasonBars(
  c: CanvasRenderingContext2D,
  ch: number,
  counts: Record<DrillReasonKey, number>,
  r: Rect,
): void {
  const rows = DRILL_REASON_ORDER.filter((k) => counts[k] > 0);
  c.save();
  c.textBaseline = 'middle';

  c.textAlign = 'left';
  c.fillStyle = UI_INK_MUTED;
  c.font = treatiseBodyFont(ch * 0.038, 600);
  c.fillText(t('summaryDrillReasons'), r.x, r.y);

  if (rows.length === 0) {
    c.fillStyle = UI_GOOD;
    c.font = treatiseBodyFont(ch * 0.04, 400);
    c.fillText('—', r.x, r.y + ch * 0.08);
    c.restore();
    return;
  }

  const max = Math.max(...rows.map((k) => counts[k]));
  const labelW = r.w * 0.3;
  const barX = r.x + labelW;
  const barAvailW = r.w - labelW - r.w * 0.12;
  const top = r.y + ch * 0.06;
  const rowH = Math.min(ch * 0.07, (r.h - ch * 0.06) / rows.length);

  c.font = treatiseBodyFont(ch * 0.034, 400);
  rows.forEach((k, i) => {
    const y = top + i * rowH + rowH * 0.5;
    const w = Math.max(2, (counts[k] / max) * barAvailW);

    c.fillStyle = UI_INK_MUTED;
    c.textAlign = 'left';
    c.fillText(reasonLabel(k), r.x, y);

    c.fillStyle = 'rgba(36,20,8,0.10)';
    c.fillRect(barX, y - rowH * 0.18, barAvailW, rowH * 0.36);
    c.fillStyle = UI_BAD;
    c.fillRect(barX, y - rowH * 0.18, w, rowH * 0.36);

    c.fillStyle = UI_INK;
    c.font = numericFont(ch * 0.034, 700);
    c.fillText(`${counts[k]}`, barX + w + 8, y);
    c.font = treatiseBodyFont(ch * 0.034, 400);
  });
  c.restore();
}

function reasonLabel(k: DrillReasonKey): string {
  switch (k) {
    case 'zona':
      return t('reasonZona');
    case 'lato':
      return t('strokeLato');
    case 'inclinazione':
      return t('strokeInclinazione');
    case 'filo':
      return t('strokeFilo');
    case 'piattonata':
      return t('filoFlat');
    case 'ambiguo':
      return t('reasonAmbiguo');
  }
}

function formatMMSS(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Cut rose + the two axes it can't show (lato/filo) as text. */
function drawTechniqueTab(
  c: CanvasRenderingContext2D,
  ch: number,
  stats: PellStats,
  area: Rect,
): void {
  const left: Rect = { x: area.x, y: area.y, w: area.w * 0.5, h: area.h };
  const right: Rect = { x: area.x + area.w * 0.52, y: area.y, w: area.w * 0.48, h: area.h };
  drawCutRose(c, ch, stats, left);
  drawStrokeBreakdownText(c, ch, stats.strokeBreakdown, right);
}

// ─────────────────────────────────────────────────────────────
// Shared drawing primitives (all area-relative)
// ─────────────────────────────────────────────────────────────

function drawStatLines(
  c: CanvasRenderingContext2D,
  ch: number,
  lines: Array<{ label: string; value: string }>,
  r: Rect,
): void {
  c.save();
  c.textBaseline = 'middle';
  const lineH = r.h / lines.length;
  lines.forEach((row, i) => {
    const y = r.y + i * lineH + lineH * 0.5;
    c.textAlign = 'left';
    c.fillStyle = UI_INK_MUTED;
    c.font = treatiseBodyFont(ch * 0.04, 400);
    c.fillText(row.label, r.x, y - lineH * 0.16);

    c.fillStyle = UI_INK;
    c.font = numericFont(ch * 0.058, 700);
    c.fillText(row.value, r.x, y + lineH * 0.22);
  });
  c.restore();
}

function drawZoneBars(
  c: CanvasRenderingContext2D,
  ch: number,
  rows: Array<{ label: string; frac: number; valueText: string; color: string; raw: number }>,
  r: Rect,
  byMax: boolean,
): void {
  const max = byMax ? Math.max(1, ...rows.map((row) => row.raw)) : 1;
  const labelW = r.w * 0.22;
  const barX = r.x + labelW;
  const barAvailW = r.w - labelW - r.w * 0.16;
  const rowH = r.h / rows.length;

  c.save();
  c.textBaseline = 'middle';
  rows.forEach((row, i) => {
    const y = r.y + i * rowH + rowH * 0.5;
    const frac = byMax ? row.raw / max : row.frac;
    const w = Math.max(2, frac * barAvailW);

    c.fillStyle = UI_INK_MUTED;
    c.font = treatiseBodyFont(ch * 0.036, 400);
    c.textAlign = 'left';
    c.fillText(row.label, r.x, y);

    c.fillStyle = 'rgba(36,20,8,0.10)';
    c.fillRect(barX, y - rowH * 0.18, barAvailW, rowH * 0.36);
    c.fillStyle = row.color;
    c.fillRect(barX, y - rowH * 0.18, w, rowH * 0.36);
    c.strokeStyle = 'rgba(36,20,8,0.55)'; // engraved ink outline
    c.lineWidth = 1;
    c.strokeRect(barX, y - rowH * 0.18, w, rowH * 0.36);

    c.fillStyle = UI_INK;
    c.font = numericFont(ch * 0.036, 700);
    c.fillText(row.valueText, barX + w + 8, y);
  });
  c.restore();
}

/**
 * Directional rose of cut angles, centered in `r`: 8 spokes (45° each), 0° = down (fendente),
 * clockwise. Spoke length scales with the cut count in that direction. The spoke shows where
 * the tip *travels* at impact (a mandritto sgualembro descends to the lower-left).
 */
function drawCutRose(c: CanvasRenderingContext2D, ch: number, stats: PellStats, r: Rect): void {
  const cx = r.x + r.w * 0.5;
  const cy = r.y + r.h * 0.42;
  const maxR = Math.min(r.w, r.h) * 0.32;
  const baseR = maxR * 0.22;
  const pad = maxR * 0.34;

  const hist = stats.cutAngleHistogram;
  const max = Math.max(1, ...hist);

  c.save();

  c.beginPath();
  c.strokeStyle = 'rgba(36,20,8,0.12)';
  c.lineWidth = 1;
  c.arc(cx, cy, maxR, 0, Math.PI * 2);
  c.stroke();

  for (let i = 0; i < CUT_ROSE_SECTORS; i++) {
    const theta = (i / CUT_ROSE_SECTORS) * Math.PI * 2;
    const dx = Math.sin(theta);
    const dy = Math.cos(theta); // canvas y grows down → sector 0 points down = fendente
    const len = baseR + (hist[i]! / max) * (maxR - baseR);
    const ex = cx + dx * len;
    const ey = cy + dy * len;

    c.beginPath();
    c.strokeStyle = 'rgba(36,20,8,0.75)'; // ink spokes, like the segno's cut lines
    c.lineWidth = 4;
    c.lineCap = 'round';
    c.moveTo(cx, cy);
    c.lineTo(ex, ey);
    c.stroke();

    if (hist[i]! > 0) {
      c.beginPath();
      c.fillStyle = UI_SEAL_RED;
      c.arc(ex, ey, 3, 0, Math.PI * 2);
      c.fill();
    }
  }

  c.fillStyle = UI_INK;
  c.font = treatiseBodyFont(ch * 0.032, 400);
  c.textBaseline = 'middle';
  c.textAlign = 'center';
  c.fillText(t('cutFendente'), cx, cy + maxR + pad);
  c.fillText(t('cutMontante'), cx, cy - maxR - pad);
  c.textAlign = 'left';
  c.fillText(t('cutTondoDx'), cx + maxR + pad * 0.4, cy);
  c.textAlign = 'right';
  c.fillText(t('cutTondoSx'), cx - maxR - pad * 0.4, cy);

  // Caption: clarify the spoke is direction of travel, not where the cut comes from.
  c.fillStyle = UI_INK_MUTED;
  c.font = treatiseBodyFont(ch * 0.028, 400);
  c.textAlign = 'center';
  c.textBaseline = 'bottom';
  c.fillText(t('summaryRoseCaption'), cx, r.y + r.h);

  c.restore();
}

/** Lato + filo distribution as text (the axes the rose can't distinguish). */
function drawStrokeBreakdownText(
  c: CanvasRenderingContext2D,
  ch: number,
  sb: StrokeBreakdown,
  r: Rect,
): void {
  if (sb.classified === 0) return;
  const amb = t('strokeAmbiguoShort');
  const lineH = ch * 0.075;
  let y = r.y + ch * 0.04;

  c.save();
  c.textBaseline = 'middle';
  c.textAlign = 'left';

  c.fillStyle = UI_INK_MUTED;
  c.font = treatiseBodyFont(ch * 0.042, 600);
  c.fillText(t('summaryStroke'), r.x, y);

  c.fillStyle = UI_INK;
  c.font = treatiseBodyFont(ch * 0.036, 400);
  y += lineH;
  c.fillText(
    `${t('strokeLato')}: mandr. ${sb.lato.mandritto} · riv. ${sb.lato.riverso} · ${amb} ${sb.lato.ambiguo}`,
    r.x,
    y,
  );
  y += lineH;
  c.fillText(
    `${t('strokeFilo')}: dritto ${sb.filo.dritto} · falso ${sb.filo.falso} · ${amb} ${sb.filo.ambiguo}`,
    r.x,
    y,
  );
  c.restore();
}

function zoneColor(z: Zone): string {
  return `#${PELL_ZONE_COLORS[z].toString(16).padStart(6, '0')}`;
}

function zoneLabel(z: Zone): string {
  switch (z) {
    case ZONE.head:
      return t('zoneHead');
    case ZONE.shoulder:
      return t('zoneShoulder');
    case ZONE.chest:
      return t('zoneChest');
    case ZONE.flank:
      return t('zoneFlank');
    case ZONE.leg:
      return t('zoneLeg');
  }
}
